import React, { useCallback, useRef } from 'react';
import classNames from 'classnames';
import bowser from 'yola-bowser';
import PropTypes from 'prop-types';
import FieldStatus from '../field-status';
import { APPEARANCE as FIELD_STATUS_APPEARANCES } from '../field-status/constants';
import { TextAreaProps } from './types';

const TextArea = ({
  inputRef = null,
  disabled = false,
  resize = false,
  invalid = false,
  selectOnFocus = false,
  defaultValue = '',
  placeholder = '',
  statusText = '',
  onChange,
  onFocus = Function.prototype,
  onBlur = Function.prototype,
  ...otherProps
}) => {
  const timer = useRef(null);
  const isMobile = bowser.mobile;
  const isTablet = bowser.tablet;
  const isTouchDevice = isMobile || isTablet;
  const classes = classNames('ws-ui-text-area', {
    'ws-ui-text-area--resize-vertical': resize && !isTouchDevice,
    'ws-ui-text-area--invalid': invalid,
    'ws-ui-text-area--mobile': isMobile,
    'ws-ui-text-area--touch-device': isTouchDevice,
  });

  const handleChange = useCallback(
    ({ target }) => {
      onChange(target.value);
    },
    [onChange]
  );

  const handleFocus = useCallback(
    ({ target }) => {
      onFocus(target.value);

      if (selectOnFocus) {
        // workaround for safari
        // link: https://stackoverflow.com/a/54229871
        timer.current = setTimeout(() => {
          target.select();
        }, 0);
      }
    },
    [onFocus, selectOnFocus]
  );

  const handleBlur = useCallback(
    ({ target }) => {
      clearTimeout(timer.current);
      onBlur(target.value);
    },
    [onBlur]
  );

  return (
    <div className="ws-ui-text-area-wrapper">
      <textarea
        {...(inputRef && { ref: inputRef })}
        key={defaultValue}
        className={classes}
        disabled={disabled}
        placeholder={placeholder}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        defaultValue={defaultValue}
        {...otherProps}
      />
      {statusText && (
        <FieldStatus
          text={statusText}
          appearance={invalid ? FIELD_STATUS_APPEARANCES.error : FIELD_STATUS_APPEARANCES.default}
        />
      )}
    </div>
  );
};

TextArea.propTypes = {
  ...TextAreaProps,
  inputRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
};

export default TextArea;
